/**
 * PROGRESS.md Parser
 *
 * Parses PROGRESS.md files to extract sprint status and feature tracking.
 */

import { readFile } from 'fs/promises';
import { existsSync } from 'fs';

// ============================================================================
// Type Definitions
// ============================================================================

export interface ProgressData {
  sprints: SprintInfo[];
  features: FeatureStatus[];
  completedFeatures: string[];
  inProgressFeatures: string[];
  notStartedFeatures: string[];
}

export interface SprintInfo {
  name: string;
  status: 'Not Started' | 'In Progress' | 'Completed';
  items: string[];
}

export interface FeatureStatus {
  name: string;
  status: 'completed' | 'in-progress' | 'not-started';
  category: string;
  checkbox: string; // The checkbox character (✅, ⬜, etc.)
}

export interface ParserOptions {
  verbose?: boolean;
  onProgress?: (message: string) => void;
}

// ============================================================================
// Parser
// ============================================================================

// Regex patterns for parsing
const CHECKBOX_COMPLETED_REGEX = /^\s*-\s*\[x\]\s*(.+)$/i;
const CHECKBOX_UNCHECKED_REGEX = /^\s*-\s*\[\s*\]\s*(.+)$/;
const EMOJI_CHECK_REGEX = /^\s*([✅✔☑])\s+(.+)$/;
const EMOJI_UNCHECKED_REGEX = /^\s*([⬜⬅○◌])\s+(.+)$/;
const SECTION_HEADER_REGEX = /^##+\s+(.+)$/;
const IN_PROGRESS_HEADER_REGEX = /##\s+(\[In Progress\]|Current Sprint)/i;

/**
 * Parse a PROGRESS.md file
 */
export async function parseProgressFile(
  filePath: string,
  options: ParserOptions = {}
): Promise<ProgressData> {
  if (!existsSync(filePath)) {
    options.onProgress?.(`PROGRESS.md not found at ${filePath}`);
    return emptyProgressData();
  }

  options.onProgress?.(`Parsing PROGRESS.md from ${filePath}`);

  const content = await readFile(filePath, 'utf-8');
  return parseProgressContent(content, options);
}

/**
 * Parse PROGRESS.md content
 */
export function parseProgressContent(
  content: string,
  options: ParserOptions = {}
): ProgressData {
  const result: ProgressData = {
    sprints: [],
    features: [],
    completedFeatures: [],
    inProgressFeatures: [],
    notStartedFeatures: [],
  };

  const lines = content.split('\n');
  let currentSection = '';
  let currentCategory = 'general';
  let inProgressSection = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Check for section headers
    const sectionMatch = line.match(SECTION_HEADER_REGEX);
    if (sectionMatch) {
      currentSection = sectionMatch[1].toLowerCase();
      options.onProgress?.(`Section: ${currentSection}`);

      // Check if this is an "In Progress" section
      inProgressSection = IN_PROGRESS_HEADER_REGEX.test(line);

      // Update category based on section
      if (currentSection.includes('backend')) {
        currentCategory = 'backend';
      } else if (currentSection.includes('frontend')) {
        currentCategory = 'frontend';
      } else if (currentSection.includes('sprint')) {
        currentCategory = 'sprint';
      } else if (currentSection.includes('feature')) {
        currentCategory = 'feature';
      } else if (currentSection.includes('epic')) {
        currentCategory = 'epic';
      } else if (currentSection.includes('completed')) {
        currentCategory = 'completed';
      } else if (currentSection.includes('current')) {
        currentCategory = 'current';
      }
      continue;
    }

    // Parse checkboxes
    const checkboxMatch = line.match(CHECKBOX_COMPLETED_REGEX) ||
                          line.match(EMOJI_CHECK_REGEX);
    const uncheckedMatch = line.match(CHECKBOX_UNCHECKED_REGEX) ||
                           line.match(EMOJI_UNCHECKED_REGEX);

    if (checkboxMatch) {
      // Completed item - extract name from different regex patterns
      // CHECKBOX_COMPLETED_REGEX: group 1 is the item name
      // EMOJI_CHECK_REGEX: group 1 is emoji, group 2 is item name
      let itemName = checkboxMatch[1]; // checkbox regex
      if (checkboxMatch[2] !== undefined) {
        itemName = checkboxMatch[2]; // emoji regex - group 2 is the name
      }
      const feature: FeatureStatus = {
        name: itemName.trim(),
        status: 'completed',
        category: currentCategory,
        checkbox: checkboxMatch[1] || checkboxMatch[2] || '✅',
      };
      result.features.push(feature);
      result.completedFeatures.push(itemName.trim());
    } else if (uncheckedMatch) {
      // Unchecked item - extract name from different regex patterns
      // CHECKBOX_UNCHECKED_REGEX: group 1 is the item name
      // EMOJI_UNCHECKED_REGEX: group 1 is emoji, group 2 is item name
      let itemName = uncheckedMatch[1]; // checkbox regex
      if (uncheckedMatch[2] !== undefined) {
        itemName = uncheckedMatch[2]; // emoji regex - group 2 is the name
      }
      const status = inProgressSection ? 'in-progress' : 'not-started';
      const feature: FeatureStatus = {
        name: itemName.trim(),
        status,
        category: currentCategory,
        checkbox: uncheckedMatch[1] || uncheckedMatch[2] || '⬜',
      };
      result.features.push(feature);

      if (status === 'in-progress') {
        result.inProgressFeatures.push(itemName.trim());
      } else {
        result.notStartedFeatures.push(itemName.trim());
      }
    }
  }

  // Extract sprint info from completed features
  if (result.completedFeatures.length > 0 || result.inProgressFeatures.length > 0) {
    result.sprints.push({
      name: 'Current Sprint',
      status: result.inProgressFeatures.length > 0 ? 'In Progress' : 'Completed',
      items: [...result.completedFeatures, ...result.inProgressFeatures],
    });
  }

  options.onProgress?.(`Parsed ${result.features.length} features`);

  return result;
}

/**
 * Create empty progress data
 */
function emptyProgressData(): ProgressData {
  return {
    sprints: [],
    features: [],
    completedFeatures: [],
    inProgressFeatures: [],
    notStartedFeatures: [],
  };
}

/**
 * Find the PROGRESS.md file in the project
 */
export function findProgressFile(root: string = process.cwd()): string {
  const possiblePaths = [
    join(root, 'docs', 'PROGRESS.md'),
    join(root, 'PROGRESS.md'),
    join(root, '.claude', 'plans', 'PROGRESS.md'),
  ];

  for (const path of possiblePaths) {
    if (existsSync(path)) {
      return path;
    }
  }

  // Default to docs/PROGRESS.md
  return join(root, 'docs', 'PROGRESS.md');
}

function join(...parts: string[]): string {
  return parts.join('/').replace(/\/+/g, '/');
}
